import React, { useState, useMemo } from 'react'
import { Tag } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons'
import OverruleReasoningModal from './OverruleReasoningModal'
import PropTypes from 'prop-types'

/**
 * MetTag - Displays whether a criterion is met, with interactive overrule capability
 * 
 * This component handles:
 * 1. Displaying the met/not met status with appropriate styling
 * 2. Showing "Overrule" on hover for better UX
 * 3. Managing its own modal visibility state
 * 4. Launching the overrule modal when clicked
 */
const MetTag = ({ 
    criterion, 
    token, 
    layerId, 
    fileId, 
    patternId, 
    existingOverrule 
}) => {
    const [showModal, setShowModal] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    // Compute tag properties based on state
    const tagProperties = useMemo(() => {
        if (isHovered) {
            return {
                text: 'Overrule',
                icon: <EditOutlined />,
                color: 'blue'
            }
        }
        
        return {
            text: criterion.met ? 'Met' : 'Not Met',
            icon: criterion.met ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
            color: criterion.met ? 'success' : 'error'
        }
    }, [isHovered, criterion.met])

    return (
        <>
            <Tag
                icon={tagProperties.icon}
                color={tagProperties.color}
                style={{ cursor: 'pointer', width: '80px', textAlign: 'center' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={() => setShowModal(true)}
                role="button"
                aria-label={`${tagProperties.text} - Click to overrule`}
                data-testid={`criterion-met-tag-${criterion.key}`}
            >
                {tagProperties.text}
            </Tag>
            
            {showModal && (
                <OverruleReasoningModal
                    onCancel={() => setShowModal(false)}
                    criterion={criterion}
                    token={token}
                    layerId={layerId}
                    fileId={fileId}
                    patternId={patternId}
                    existingOverrule={existingOverrule}
                />
            )}
        </>
    )
}

MetTag.propTypes = {
    criterion: PropTypes.shape({
        key: PropTypes.number.isRequired,
        met: PropTypes.bool.isRequired,
        reasoning: PropTypes.string.isRequired
    }).isRequired,
    token: PropTypes.string.isRequired,
    layerId: PropTypes.string.isRequired,
    fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    patternId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    existingOverrule: PropTypes.object
}

export default MetTag 